import { useQuery } from "@tanstack/react-query";
import { Form, Formik, FormikValues } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useAtomValue } from "jotai";

import { Button, Input, Select } from "../../../custom";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import api from "../../../utils/api";
import { userAtom } from "../../../utils/store";
import { routes } from "../../../routes";
import useChangeCourseMutation from "../../../hooks/api/useChangeCourseMutation";
import useGetSpecializationByProgramIdQuery from "../../../hooks/api/useGetSpecializationByProgramIdQuery";
import styles from "./styles.module.scss";

interface ChangeCourseFormValues {
  newProgramId: string;
  newConcentrationId: string;
  reason: string;
}

const validationSchema = Yup.object().shape({
  newProgramId: Yup.string().required("Program is required"),
  reason: Yup.string().trim().required("Reason is required"),
});

const getAllProgramDetails = async () => {
  return (await api.get("/Utilities/Utilities/GetAllProgramDetails"))
    .data as ProgramDataResponse;
};

const ChangeCourse = () => {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const [selectedProgramId, setSelectedProgramId] = useState(0);
  const changeCourseMutation = useChangeCourseMutation();

  const programQuery = useQuery({
    queryKey: ["get-program-details"],
    queryFn: getAllProgramDetails,
  });

  const specializationQuery = useGetSpecializationByProgramIdQuery({
    Id: selectedProgramId,
  });

  const programs =
    programQuery.data?.data?.filter((program) => program.activeStatus && program.programId == user?.programId && program.programTypeId == user?.programTypeId) ?? [];

  const programOptions = programs.map((program) => (
    <option key={program.id} value={program.id}>
      {program.programName}
    </option>
  ));

  const concentrationOptions = specializationQuery.specializations?.map(
    (concentration) => (
      <option key={concentration.id} value={concentration.id}>
        {concentration.name}
      </option>
    ),
  );

  const handleSubmit = async (values: FormikValues) => {
    await changeCourseMutation.changeCourseHandler({
      applicantId: user?.applicantId ?? "",
      newProgramId: Number(values.newProgramId),
      newConcentrationId: Number(values.newConcentrationId) || null,
      reason: values.reason,
    });

    navigate(routes.dashboard.dashboard);
  };

  return (
    <PageLayout
      heading="Confirm Your Program"
      paragraph="Your application was submitted before we started tracking a unique program code, so we can't be fully certain it's tied to the right course. Please confirm or update your program below."
    >
      <div className={styles.container}>
        <Formik<ChangeCourseFormValues>
          initialValues={{
            newProgramId: "",
            newConcentrationId: "",
            reason: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, setFieldValue }) => (
            <Form className={styles.form}>
              <Select
                name="newProgramId"
                label="Program"
                placeholder={
                  programQuery.isLoading ? "Loading programs" : "Select program"
                }
                options={programOptions}
                disabled={programQuery.isLoading}
                onChange={(event) => {
                  handleChange(event);
                  const programId = Number(event.target.value);
                  setSelectedProgramId(programId);
                  setFieldValue("newConcentrationId", "");
                }}
              />
              <Select
                name="newConcentrationId"
                label="Concentration"
                placeholder={
                  specializationQuery.isLoading
                    ? "Loading concentrations"
                    : "Select concentration"
                }
                options={concentrationOptions}
                disabled={!selectedProgramId || specializationQuery.isLoading}
              />
              <Input
                type="textarea"
                name="reason"
                label="Reason"
                placeholder="e.g. Confirming my program after a system update"
              />

              <div className={styles.btnWrapper}>
                <Button
                  type="submit"
                  text="CONFIRM PROGRAM"
                  disabled={changeCourseMutation.isLoading}
                  isLoading={changeCourseMutation.isLoading}
                />
                <Button
                  type="button"
                  text="Skip for now"
                  className={styles.skipButton}
                  onClick={() => navigate(routes.dashboard.dashboard)}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PageLayout>
  );
};

export default ChangeCourse;
